import React, { useEffect, useMemo, useState } from 'react';
import { DemeterPermissionType, DemeterSubscriptionFrequency, DemeterUserType } from '../../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../../Redux/ReduxStore';
import { selectUserType } from '../../../../Redux/Slices/UserSlice';
import CheckboxInput from '../../../Components/Form/Inputs/CheckboxInput';
import DatePickerInput from '../../../Components/Form/Inputs/DatePickerInput';
import SelectInput from '../../../Components/Form/Inputs/SelectInput';
import TextInput from '../../../Components/Form/Inputs/TextInput';
import LoadingSpinner from '../../../Components/LoadingSpinner/LoadingSpinner';
import RegexValidators from '../../../Core/Validation/RegexValidators';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import usePermission from '../../../Services/Permissions/usePermissionHook';
import styles from './CompaniesPage.module.scss';

export type UpsertCompanyDetailsRequest = {
    address1: string;
    address2: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
    phone: string;
    contactPerson: string;
    demeterCompanyGuid?: string;
    name?: string;
    invoiceDate?: string | Date | null;
    subscriptionPrice?: number | null;
    subscriptionFrequency?: DemeterSubscriptionFrequency | string | null;
    policyNumbers?: string;
    notes?: string | null;
    termsAndConditionsAccepted?: boolean;
    termsAndConditionsAcceptedAt?: string | Date;
};

interface ICompanyDetailsFormProps {
    companyDetails: UpsertCompanyDetailsRequest;
    handleCompanyDetailsIsValid: (isValid: boolean) => void;
    handleCompanyDetailsChange: (companyDetails: UpsertCompanyDetailsRequest) => void;
}

const CompanyDetailsForm = (props: ICompanyDetailsFormProps) => {
    const [translations] = useLanguage();
    const currentUserType = useApplicationSelector(selectUserType);
    const [companyDetails, setCompanyDetails] = useState<UpsertCompanyDetailsRequest>();
    const isBusinessOwnerOrAdministrator = useMemo(
        () => currentUserType === DemeterUserType.BusinessOwner || currentUserType === DemeterUserType.Administrator,
        [currentUserType],
    );

    const subscriptionFrequencyOptions = useMemo(
        () => [
            {
                value: undefined,
                label: '',
            },
            {
                value: DemeterSubscriptionFrequency.Monthly,
                label: translations.subscriptionFrequency.Monthly,
            },
            {
                value: DemeterSubscriptionFrequency.Quarterly,
                label: translations.subscriptionFrequency.Quarterly,
            },
            {
                value: DemeterSubscriptionFrequency.Yearly,
                label: translations.subscriptionFrequency.Yearly,
            },
        ],
        [translations],
    );

    const showPolicyNumbers = usePermission(DemeterPermissionType.FarmerMarginAdministrator);

    useEffect(() => {
        if (
            (isBusinessOwnerOrAdministrator && companyDetails) ||
            (companyDetails &&
                !!companyDetails.address1 &&
                !!companyDetails.city &&
                !!companyDetails.state &&
                !!companyDetails.country &&
                !!companyDetails.postalCode &&
                !!companyDetails.phone &&
                !!companyDetails.contactPerson &&
                (props.companyDetails.demeterCompanyGuid === undefined || !!companyDetails.demeterCompanyGuid) &&
                (props.companyDetails.name === undefined || !!companyDetails.name) &&
                (props.companyDetails.termsAndConditionsAcceptedAt === undefined || !!companyDetails.termsAndConditionsAcceptedAt) &&
                (props.companyDetails.termsAndConditionsAccepted === undefined || !!companyDetails.termsAndConditionsAccepted))
        ) {
            props.handleCompanyDetailsIsValid(true);
            props.handleCompanyDetailsChange(companyDetails);
            return;
        }

        props.handleCompanyDetailsIsValid(false);
    }, [companyDetails]);

    useEffect(() => {
        if (companyDetails) {
            return;
        }

        setCompanyDetails(props.companyDetails);
    }, [props.companyDetails]);

    return !companyDetails ? (
        <LoadingSpinner />
    ) : (
        <div>
            {props.companyDetails.name !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <TextInput
                        title={translations.companies.fields.company}
                        placeholder={translations.companies.fields.company}
                        required={!isBusinessOwnerOrAdministrator}
                        denoteRequired={!isBusinessOwnerOrAdministrator}
                        value={companyDetails?.name}
                        handleTextChange={(value) => setCompanyDetails({ ...companyDetails, name: value })}
                        validation={RegexValidators.AlphaNumericMinimumLength2}
                        errorMessage={translations.companies.errors.requiredField}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'CompanyNameChanged',
                            eventValue: companyDetails?.name,
                        }}
                    />
                </div>
            )}
            {props.companyDetails.address1 !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <TextInput
                        title={translations.companies.fields.address1}
                        placeholder={translations.companies.fields.address1}
                        required={!isBusinessOwnerOrAdministrator}
                        denoteRequired={!isBusinessOwnerOrAdministrator}
                        value={companyDetails?.address1}
                        handleTextChange={(value) => setCompanyDetails({ ...companyDetails, address1: value })}
                        validation={RegexValidators.AlphaNumericMinimumLength2}
                        errorMessage={translations.companies.errors.requiredField}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'Address1Changed',
                            eventValue: companyDetails?.address1,
                        }}
                    />
                </div>
            )}
            {props.companyDetails.address2 !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <TextInput
                        title={translations.companies.fields.address2}
                        placeholder={translations.companies.fields.address2}
                        value={companyDetails?.address2}
                        handleTextChange={(value) => setCompanyDetails({ ...companyDetails, address2: value })}
                        validation={RegexValidators.AlphaNumericMinimumLength2}
                        errorMessage={translations.companies.errors.requiredField}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'Address2Changed',
                            eventValue: companyDetails?.address2,
                        }}
                    />
                </div>
            )}
            {props.companyDetails.city !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <TextInput
                        title={translations.companies.fields.city}
                        placeholder={translations.companies.fields.city}
                        required={!isBusinessOwnerOrAdministrator}
                        denoteRequired={!isBusinessOwnerOrAdministrator}
                        value={companyDetails?.city}
                        handleTextChange={(value) => setCompanyDetails({ ...companyDetails, city: value })}
                        validation={RegexValidators.AlphaNumericMinimumLength2}
                        errorMessage={translations.companies.errors.requiredField}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'CityChanged',
                            eventValue: companyDetails?.city,
                        }}
                    />
                </div>
            )}
            {props.companyDetails.state !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <TextInput
                        title={translations.companies.fields.state}
                        placeholder={translations.companies.fields.state}
                        required={!isBusinessOwnerOrAdministrator}
                        denoteRequired={!isBusinessOwnerOrAdministrator}
                        value={companyDetails?.state}
                        handleTextChange={(value) => setCompanyDetails({ ...companyDetails, state: value })}
                        validation={RegexValidators.AlphaNumericMinimumLength2}
                        errorMessage={translations.companies.errors.requiredField}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'StateChanged',
                            eventValue: companyDetails?.state,
                        }}
                    />
                </div>
            )}
            {props.companyDetails.country !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <TextInput
                        title={translations.companies.fields.country}
                        placeholder={translations.companies.fields.country}
                        required={!isBusinessOwnerOrAdministrator}
                        denoteRequired={!isBusinessOwnerOrAdministrator}
                        value={companyDetails?.country}
                        handleTextChange={(value) => setCompanyDetails({ ...companyDetails, country: value })}
                        validation={RegexValidators.AlphaNumericMinimumLength2}
                        errorMessage={translations.companies.errors.requiredField}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'CountryChanged',
                            eventValue: companyDetails?.country,
                        }}
                    />
                </div>
            )}
            {props.companyDetails.postalCode !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <TextInput
                        title={translations.companies.fields.postalCode}
                        placeholder={translations.companies.fields.postalCode}
                        required={!isBusinessOwnerOrAdministrator}
                        denoteRequired={!isBusinessOwnerOrAdministrator}
                        value={companyDetails?.postalCode}
                        handleTextChange={(value) => setCompanyDetails({ ...companyDetails, postalCode: value })}
                        validation={RegexValidators.AlphaNumericMinimumLength2}
                        errorMessage={translations.companies.errors.requiredField}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'PostalCodeChanged',
                            eventValue: companyDetails?.postalCode,
                        }}
                    />
                </div>
            )}
            {props.companyDetails.phone !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <TextInput
                        title={translations.companies.fields.phone}
                        placeholder={translations.companies.fields.phone}
                        required={!isBusinessOwnerOrAdministrator}
                        denoteRequired={!isBusinessOwnerOrAdministrator}
                        value={companyDetails?.phone}
                        handleTextChange={(value) => setCompanyDetails({ ...companyDetails, phone: value })}
                        validation={RegexValidators.Phone}
                        errorMessage={translations.companies.errors.requiredField}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'PhoneChanged',
                            eventValue: companyDetails?.phone,
                        }}
                    />
                </div>
            )}
            {props.companyDetails.contactPerson !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <TextInput
                        title={translations.companies.fields.contactPerson}
                        placeholder={translations.companies.fields.contactPerson}
                        required={!isBusinessOwnerOrAdministrator}
                        denoteRequired={!isBusinessOwnerOrAdministrator}
                        value={companyDetails?.contactPerson}
                        handleTextChange={(value) => setCompanyDetails({ ...companyDetails, contactPerson: value })}
                        validation={RegexValidators.AlphaNumericMinimumLength2}
                        errorMessage={translations.companies.errors.requiredField}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'ContactPersonChanged',
                            eventValue: companyDetails?.contactPerson,
                        }}
                    />
                </div>
            )}
            {currentUserType !== DemeterUserType.BusinessOwner &&
                props.companyDetails.termsAndConditionsAccepted &&
                props.companyDetails.termsAndConditionsAcceptedAt && (
                    <div className={styles.add_edit_company_input}>
                        <div className={styles.add_edit_company_checkbox_input}>
                            <CheckboxInput
                                title={translations.companies.fields.termsAndConditionsAccepted}
                                isChecked={companyDetails?.termsAndConditionsAccepted ?? false}
                                disabled
                                track={{
                                    eventCategory: 'Company',
                                    eventAction: 'TermsAndConditionsAcceptedChanged',
                                    eventValue: companyDetails?.termsAndConditionsAccepted,
                                }}
                            />
                        </div>
                        {companyDetails?.termsAndConditionsAccepted && companyDetails?.termsAndConditionsAcceptedAt && (
                            <div>
                                {translations.companies.fields.termsAndConditionsAcceptedAt}{' '}
                                {formattingService.toShortDayMonthYear(new Date(companyDetails?.termsAndConditionsAcceptedAt))}
                            </div>
                        )}
                    </div>
                )}

            {props.companyDetails.invoiceDate !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <DatePickerInput
                        name="CompanyInvoiceDate"
                        title={translations.companies.fields.invoiceDate}
                        value={companyDetails?.invoiceDate as Date}
                        handleDateChange={(value) => value && setCompanyDetails({ ...companyDetails, invoiceDate: value })}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'InvoiceDateChanged',
                            eventValue: companyDetails?.invoiceDate,
                        }}
                    />
                </div>
            )}
            {props.companyDetails.subscriptionPrice !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <TextInput
                        title={translations.companies.fields.subscriptionPrice}
                        placeholder={translations.companies.fields.subscriptionPrice}
                        type="number"
                        value={companyDetails?.subscriptionPrice?.toString()}
                        handleTextChange={(value) => setCompanyDetails({ ...companyDetails, subscriptionPrice: +value })}
                        validation={RegexValidators.Numeric}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'SubscriptionPriceChanged',
                            eventValue: companyDetails?.subscriptionPrice,
                        }}
                    />
                </div>
            )}
            {props.companyDetails.subscriptionFrequency !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <SelectInput
                        title={translations.companies.fields.subscriptionFrequency}
                        value={companyDetails?.subscriptionFrequency}
                        options={subscriptionFrequencyOptions}
                        handleOptionChange={(value) => setCompanyDetails({ ...companyDetails, subscriptionFrequency: value })}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'SubscriptionFrequencyChanged',
                            eventValue: companyDetails?.subscriptionFrequency,
                        }}
                    />
                </div>
            )}
            {showPolicyNumbers && props.companyDetails.policyNumbers !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <TextInput
                        title={translations.companies.fields.policyNumbers}
                        placeholder={translations.companies.fields.policyNumbers}
                        value={companyDetails?.policyNumbers}
                        handleTextChange={(value) => setCompanyDetails({ ...companyDetails, policyNumbers: value })}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'PolicyNumbersChanged',
                            eventValue: companyDetails?.policyNumbers,
                        }}
                    />
                </div>
            )}
            {isBusinessOwnerOrAdministrator && props.companyDetails.notes !== undefined && (
                <div className={styles.add_edit_company_input}>
                    <TextInput
                        title={translations.companies.fields.notes}
                        placeholder={translations.companies.fields.notes}
                        value={companyDetails?.notes ?? ''}
                        handleTextChange={(value) => setCompanyDetails({ ...companyDetails, notes: value })}
                        track={{
                            eventCategory: 'Company',
                            eventAction: 'NotesChanged',
                            eventValue: companyDetails?.notes,
                        }}
                    />
                </div>
            )}
            {!isBusinessOwnerOrAdministrator && (
                <div className={styles.add_edit_company_input}>
                    <em>* {translations.companies.text.indicatesRequiredField}</em>
                </div>
            )}
        </div>
    );
};

export default CompanyDetailsForm;
